import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import {
  type ShopSplashScreen,
  type ShopSplashScreenInput,
  type SplashFrequency,
  listSplashScreens,
  createSplashScreen,
  updateSplashScreen,
  deleteSplashScreen,
} from '@/services/shopAdminApi';
import { Plus, Trash2, Save, X, AlertCircle, CheckCircle2, Loader2, Image as ImageIcon, Clock } from 'lucide-react';

const EMPTY: ShopSplashScreenInput = {
  title: '',
  body: '',
  image_url: '',
  cta_text: 'Shop Now',
  cta_link: '',
  background_color: '#F7F5EE',
  text_color: '#1A2E12',
  display_frequency: 'once_per_session',
  delay_seconds: 0,
  is_active: true,
  starts_at: null,
  ends_at: null,
};

export function SplashScreensTab() {
  const [items, setItems] = useState<ShopSplashScreen[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ShopSplashScreenInput>(EMPTY);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await listSplashScreens();
      setItems(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load splash screens');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startCreate() {
    setEditingId('new');
    setForm(EMPTY);
  }

  function startEdit(s: ShopSplashScreen) {
    setEditingId(s.id);
    setForm({
      title: s.title,
      body: s.body || '',
      image_url: s.image_url || '',
      cta_text: s.cta_text || '',
      cta_link: s.cta_link || '',
      background_color: s.background_color,
      text_color: s.text_color,
      display_frequency: s.display_frequency,
      delay_seconds: s.delay_seconds,
      is_active: s.is_active,
      starts_at: s.starts_at,
      ends_at: s.ends_at,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY);
  }

  function update<K extends keyof ShopSplashScreenInput>(key: K, value: ShopSplashScreenInput[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setMessage(null);
    try {
      if (editingId === 'new') {
        await createSplashScreen(form);
        setMessage({ type: 'success', text: 'Splash screen created.' });
      } else if (editingId) {
        await updateSplashScreen(editingId, form);
        setMessage({ type: 'success', text: 'Splash screen updated.' });
      }
      setEditingId(null);
      setForm(EMPTY);
      await load();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this splash screen?')) return;
    try {
      await deleteSplashScreen(id);
      await load();
      setMessage({ type: 'success', text: 'Deleted.' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Delete failed.' });
    }
  }

  if (loading) return <div className="p-8 text-center text-[#6B7B6E]">Loading splash screens…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1A2E12]">Splash Screens</h2>
        <button
          onClick={startCreate}
          disabled={editingId === 'new'}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#BDD630] text-[#1A2E12] rounded-lg text-sm font-semibold hover:bg-[#96c23d] disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Add Splash Screen
        </button>
      </div>

      {message && (
        <div className={cn(
          'flex items-start gap-2 rounded-lg px-4 py-3 text-sm',
          message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
        )}>
          {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertCircle className="w-4 h-4 mt-0.5" />}
          {message.text}
        </div>
      )}

      {editingId === 'new' && (
        <SplashForm form={form} update={update} saving={saving} onSave={handleSave} onCancel={cancelEdit} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && <p className="text-sm text-[#6B7B6E]">No splash screens yet.</p>}
        {items.map(s => (
          <div key={s.id} className="bg-white rounded-xl border border-[#D9B38C]/20 overflow-hidden">
            {editingId === s.id ? (
              <SplashForm form={form} update={update} saving={saving} onSave={handleSave} onCancel={cancelEdit} />
            ) : (
              <div className="p-4 space-y-3">
                <div
                  className="rounded-lg p-6 text-center"
                  style={{ backgroundColor: s.background_color, color: s.text_color }}
                >
                  <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-70" />
                  <p className="font-semibold text-lg">{s.title}</p>
                  {s.body && <p className="text-sm opacity-90 mt-1">{s.body}</p>}
                  {s.cta_text && <span className="inline-block mt-3 px-4 py-1 rounded-full text-xs font-semibold bg-white/20">{s.cta_text}</span>}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-2 text-xs text-[#6B7B6E]">
                    <span className="px-2 py-1 bg-[#F7F5EE] rounded flex items-center gap-1"><Clock className="w-3 h-3" /> {s.display_frequency.replace(/_/g, ' ')}</span>
                    {s.delay_seconds > 0 && <span className="px-2 py-1 bg-[#F7F5EE] rounded">delay {s.delay_seconds}s</span>}
                    <span className={cn('px-2 py-1 rounded', s.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600')}>{s.is_active ? 'Active' : 'Inactive'}</span>
                    {s.starts_at && <span className="px-2 py-1 bg-[#F7F5EE] rounded">from {s.starts_at.slice(0, 10)}</span>}
                    {s.ends_at && <span className="px-2 py-1 bg-[#F7F5EE] rounded">until {s.ends_at.slice(0, 10)}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(s)} className="p-2 text-[#6B7B6E] hover:text-[#1A2E12] hover:bg-[#F7F5EE] rounded-lg"><Save className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(s.id)} className="p-2 text-[#6B7B6E] hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SplashForm({
  form,
  update,
  saving,
  onSave,
  onCancel,
}: {
  form: ShopSplashScreenInput;
  update: <K extends keyof ShopSplashScreenInput>(key: K, value: ShopSplashScreenInput[K]) => void;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="bg-[#F7F5EE] rounded-xl p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Title" value={form.title} onChange={v => update('title', v)} />
        <Input label="CTA text" value={form.cta_text || ''} onChange={v => update('cta_text', v)} />
        <Input label="Image URL" value={form.image_url || ''} onChange={v => update('image_url', v)} />
        <Input label="CTA link" value={form.cta_link || ''} onChange={v => update('cta_link', v)} />
        <ColorInput label="Background" value={form.background_color} onChange={v => update('background_color', v)} />
        <ColorInput label="Text color" value={form.text_color} onChange={v => update('text_color', v)} />
        <Select label="Display frequency" value={form.display_frequency} onChange={v => update('display_frequency', v as SplashFrequency)} options={[{ value: 'once_per_session', label: 'Once per session' }, { value: 'every_visit', label: 'Every visit' }]} />
        <Input label="Delay seconds" type="number" value={String(form.delay_seconds)} onChange={v => update('delay_seconds', parseInt(v || '0', 10))} />
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-[#6B7B6E]">Body</label>
          <textarea
            rows={3}
            value={form.body || ''}
            onChange={e => update('body', e.target.value)}
            className="w-full mt-1 px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630]"
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-[#1A2E12]">
            <input type="checkbox" checked={form.is_active} onChange={e => update('is_active', e.target.checked)} />
            Active
          </label>
        </div>
        <Input label="Starts at" type="datetime-local" value={form.starts_at ? form.starts_at.slice(0, 16) : ''} onChange={v => update('starts_at', v ? new Date(v).toISOString() : null)} />
        <Input label="Ends at" type="datetime-local" value={form.ends_at ? form.ends_at.slice(0, 16) : ''} onChange={v => update('ends_at', v ? new Date(v).toISOString() : null)} />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-[#6B7B6E] hover:bg-white rounded-lg"><X className="w-4 h-4 inline mr-1" /> Cancel</button>
        <button onClick={onSave} disabled={saving} className="px-4 py-2 bg-[#BDD630] text-[#1A2E12] rounded-lg text-sm font-semibold hover:bg-[#96c23d] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <Save className="w-4 h-4 inline mr-1" />} Save
        </button>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630]"
      />
    </div>
  );
}

function ColorInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-10 h-9 p-1 rounded border" />
        <input type="text" value={value} onChange={e => onChange(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm" />
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold text-[#6B7B6E]">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-[#D9B38C]/30 text-sm focus:outline-none focus:ring-2 focus:ring-[#BDD630] bg-white"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
